import React from "react";
import { Container, Typography, useTheme, Grid, Box, Button } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DirectionsIcon from "@mui/icons-material/Directions";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CONTACT_INFO from "../../constants/contactInfo";
import VideoCard from "../../components/VideoCard";
import Reveal from "../../components/Reveal";
import { PageHero, Section, SectionTitle, ZoomImage } from "../../components/layout";

const HERO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/exteriores/WhatsApp%20Image%202025-01-14%20at%2017.31.34.jpeg?updatedAt=1737568008715";
const CARTEL_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/como-llegar/WhatsApp%20Image%202025-01-22%20at%2020.42.14.jpeg?updatedAt=1737730436962";

const DATOS = [
  { icon: ScheduleIcon, value: "~2 h", label: "desde la ciudad de Córdoba" },
  { icon: PlaceOutlinedIcon, value: "16 km", label: "de Villa General Belgrano" },
  { icon: PlaceOutlinedIcon, value: "15 km", label: "de La Cumbrecita" },
  { icon: SpeedIcon, value: "30 km/h", label: "máx. en el camino de tierra" },
];

const PASOS = [
  {
    titulo: "Salí por la RP5",
    texto: "Desde la ciudad de Córdoba, tomá la Ruta Provincial 5 (RP5) hacia Alta Gracia.",
  },
  {
    titulo: "Rotonda de Villa del Prado",
    texto:
      "Al pasar Villa del Prado, en la rotonda tomá la tercera salida hacia Villa Ciudad América (AUR5) y continuá por la RP5 en dirección a Villa General Belgrano.",
  },
  {
    titulo: "Tomá la RP109",
    texto: "Desde la RP5, tomá la Ruta Provincial 109 (RP109) hacia La Cumbrecita.",
  },
  {
    titulo: "Seguí el cartel",
    texto:
      "Aproximadamente 6 km después, encontrarás una bifurcación de tierra. Allí verás el cartel que indica el ingreso al camino hacia las cabañas.",
    aviso: "Este tramo puede ser angosto y requiere conducir a una velocidad máxima de 30 km/h.",
    imagen: CARTEL_IMAGE,
  },
];

export default function ComoLlegar() {
  const theme = useTheme();

  return (
    <Box>
      <PageHero image={HERO_IMAGE} imagePosition="center 30%" overline="Cómo llegar" title="El camino a las cabañas">
        En Athos Pampa, Valle de Calamuchita, a unas 2 horas de la ciudad de Córdoba.
      </PageHero>

      {/* Datos rápidos */}
      <Container sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 1 }}>
        <Reveal>
          <Box
            sx={{
              backgroundColor: "background.paper",
              borderRadius: "20px",
              boxShadow: "0 20px 50px -20px rgba(35,31,32,0.25)",
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 3, md: 2 },
              p: { xs: 3, md: 4 },
            }}
          >
            {DATOS.map(({ icon: Icon, value, label }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "rgba(203,110,40,0.12)",
                    color: "primary.main",
                  }}
                >
                  <Icon />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: theme.typography.h1.fontFamily,
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      lineHeight: 1.1,
                    }}
                  >
                    {value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                    {label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Reveal>
      </Container>

      {/* Paso a paso */}
      <Section>
        <Container>
          <Grid container spacing={{ xs: 6, md: 10 }}>
            <Grid item xs={12} md={7}>
              <Reveal>
                <SectionTitle overline="Paso a paso" title="Desde Córdoba">
                  Seguí estas indicaciones y vas a llegar sin problemas. El viaje
                  completo dura aproximadamente 2 horas.
                </SectionTitle>
              </Reveal>
              <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {PASOS.map((paso, index) => {
                  const last = index === PASOS.length - 1;
                  return (
                    <Reveal key={paso.titulo} delay={index * 80}>
                      <Box
                        component="li"
                        sx={{
                          display: "flex",
                          gap: { xs: 2, md: 3 },
                          position: "relative",
                          pb: last ? 0 : 5,
                          // Línea que une los números
                          "&::before": last
                            ? undefined
                            : {
                                content: '""',
                                position: "absolute",
                                left: 21,
                                top: 48,
                                bottom: 4,
                                width: 2,
                                backgroundColor: theme.palette.sand.dark,
                              },
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            backgroundColor: last ? "primary.main" : "secondary.dark",
                            color: "white",
                            fontFamily: theme.typography.h1.fontFamily,
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ pt: 1, flex: 1 }}>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            {paso.titulo}
                          </Typography>
                          <Typography variant="body1" sx={{ color: "text.secondary" }}>
                            {paso.texto}
                          </Typography>
                          {paso.aviso && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "flex-start",
                                mt: 2,
                                p: 2,
                                borderRadius: "16px",
                                backgroundColor: "rgba(203,110,40,0.08)",
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                              }}
                            >
                              <WarningAmberIcon sx={{ color: "primary.main" }} fontSize="small" />
                              <Typography variant="body2">{paso.aviso}</Typography>
                            </Box>
                          )}
                          {paso.imagen && (
                            <ZoomImage sx={{ mt: 3, height: { xs: 220, md: 300 }, borderRadius: "20px" }}>
                              <img src={paso.imagen} alt="Cartel de ingreso a Cabañas del Campo" loading="lazy" />
                            </ZoomImage>
                          )}
                        </Box>
                      </Box>
                    </Reveal>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: { md: "sticky" }, top: { md: 110 } }}>
                <Reveal delay={150}>
                  <Typography variant="overline" color="primary" component="p" sx={{ textAlign: "center", mb: 2 }}>
                    Mirá el recorrido
                  </Typography>
                  <VideoCard videoUrl="https://www.youtube.com/embed/N1GPYiq6TOk?autoplay=1&mute=1" />
                </Reveal>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Mapa */}
      <Section sx={{ backgroundColor: theme.palette.sand.light, mb: "-20px" }}>
        <Container>
          <Reveal>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "flex-end" },
                justifyContent: "space-between",
                gap: 3,
                mb: { xs: 4, md: 5 },
              }}
            >
              <SectionTitle overline="Ubicación exacta" title="Te esperamos acá" sx={{ mb: 0 }}>
                Usá Google Maps para obtener las indicaciones exactas desde donde estés.
              </SectionTitle>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flexShrink: 0 }}>
                <Button
                  variant="contained"
                  startIcon={<DirectionsIcon />}
                  href={CONTACT_INFO.googleMapsDirections}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cómo llegar en Google Maps
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  href={CONTACT_INFO.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ¿Dudas? Escribinos
                </Button>
              </Box>
            </Box>
          </Reveal>
          <Reveal delay={100}>
            <Box
              sx={{
                position: "relative",
                height: { xs: 360, md: 480 },
                borderRadius: "24px",
                overflow: "hidden",
                boxShadow: "0 20px 50px -20px rgba(35,31,32,0.25)",
              }}
            >
              <iframe
                src={CONTACT_INFO.googleMapsLink}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de Cabañas del Campo en Athos Pampa"
              />
            </Box>
          </Reveal>
        </Container>
      </Section>
    </Box>
  );
}
